import { useEffect, useMemo, useState } from 'react';
import { useColorMode } from '../../../shared/features/theme/components/ColorModeProvider';
import { useZonesMapConfig } from '../../zones/public/use-zones-map-config';
import { useZonesOverlay } from '../../zones/public/use-zones-overlay';
import { TRUCK_SELECTION_LAYER_ID } from '../adapters/truck-layers';
import { filterLiveTrucks, isFilteringLiveTrucks, NO_LIVE_TRUCK_FILTERS, type LiveTruckFilters } from '../policies/live-truck-filters';
import { useLiveMap } from './use-live-map';
import { useLiveTrucks } from './use-live-trucks';

/** Statuses follow the clock, so the map re-derives them this often even when no fix arrives. */
export const CLOCK_TICK_MS = 15_000;

function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, []);
  return now;
}

/**
 * Everything the live map page shows: the trucks, the filters over them, the open truck, and the map that draws them. The map's
 * container ref is returned beside `page`, not inside it, so the page can read `page` while rendering.
 */
export function useLiveMapPage() {
  const config = useZonesMapConfig();
  const { mode } = useColorMode();
  const { state, connection } = useLiveTrucks();
  const now = useNow();
  const [filters, setFilters] = useState<LiveTruckFilters>(NO_LIVE_TRUCK_FILTERS);
  const [selectedTruckId, setSelectedTruckId] = useState<string | null>(null);

  const allTrucks = useMemo(() => (state.status === 'loaded' ? state.trucks : []), [state]);
  const shownTrucks = useMemo(() => filterLiveTrucks(allTrucks, filters, now), [allTrucks, filters, now]);
  const tileKey = config === undefined ? undefined : config.tileKey;

  const { containerRef, map } = useLiveMap({ tileKey: tileKey ?? null, mode, trucks: shownTrucks, now, selectedTruckId, onSelect: setSelectedTruckId });
  useZonesOverlay(map, mode, TRUCK_SELECTION_LAYER_ID);

  const page = {
    tileKey,
    state,
    connection,
    now,
    filters,
    setFilters,
    isFiltered: isFilteringLiveTrucks(filters),
    shownCount: shownTrucks.length,
    totalCount: allTrucks.length,
    selectedTruck: allTrucks.find((truck) => truck.truckId === selectedTruckId) ?? null,
    closeTruck: () => setSelectedTruckId(null),
  };
  return { containerRef, page };
}

export type LiveMapPageView = ReturnType<typeof useLiveMapPage>['page'];
